import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import Cookie from 'js-cookie'
import { useUserContext } from '../context/UserContext'
import AuthApi from '../api/AuthApi'
import ClientAuthApi from '../api/ClientAuthApi'
import { ACCESS_TOKEN } from '../constants/userConstants'
import RoutePermission from '../guards/RoutePermission'
import moduleNavAclHelp from '../lib/moduleNavAclHelp'
import { getClinicModeFlags } from '../config/clinicMode'

const WELLNESS_BLOCKED_ROUTE_PREFIXES = [
  '/orders',
  '/order',
  '/purchaseOrder',
  '/treatments',
  '/inventory',
  '/lowStockList'
]

const isBlockedRouteInWellness = (pathname = '') => {
  return WELLNESS_BLOCKED_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

/* eslint-disable react/prop-types */
const AuthGuard = ({ component, navId }) => {
  const navigate = useNavigate()
  const { getSessionUser, updateSessionUser, getAclNavIds, updateAclNavIds } = useUserContext()
  const location = useLocation()
  const path = location?.pathname?.split('/')[1] || ''
  const pathname = location?.pathname || ''
  const isClientPortal = path === 'clientSchedule' || path === 'clientBooking' || path === 'clientSettings'
  const isOpenClientBooking = path === 'clientBooking'

  const cleanLogin = (tab = 'staff') => {
    Cookie.remove(ACCESS_TOKEN)
    window.location.href = tab === 'client' ? '/clientBooking' : '/login'
  }

  useEffect(() => {
    const loadClientSession = async () => {
      try {
        const client =
          (ClientAuthApi.getSessionClient && (await ClientAuthApi.getSessionClient())) ||
          (ClientAuthApi.getProfile && (await ClientAuthApi.getProfile())) ||
          (ClientAuthApi.getClientSession && (await ClientAuthApi.getClientSession())) ||
          null

        if (client) {
          await updateSessionUser(client)
          await updateAclNavIds([])
          return true
        }
      } catch (e) {
        // Client booking may continue without a session and show phone verification.
      }
      return false
    }

    const loadStaffSession = async () => {
      try {
        const staff = await AuthApi.getSessionMember()
        if (!staff) return null

        let memberAcls = []
        if (moduleNavAclHelp.hasModuleRole(staff)) {
          memberAcls = await moduleNavAclHelp.getMemberAclList()
        }

        await updateSessionUser(staff)
        await updateAclNavIds(memberAcls)
        return { staff, memberAcls }
      } catch (e) {
        return null
      }
    }

    const redirect404IfStaffBlocked = (sessionUser, memberAcls = []) => {
      const { isWellnessEffective } = getClinicModeFlags()
      if (isWellnessEffective && isBlockedRouteInWellness(pathname)) {
        window.location.href = '/404'
        return true
      }
      if (!RoutePermission.checkItemAllow(sessionUser, path, memberAcls)) {
        window.location.href = '/404'
        return true
      }
      return false
    }

    const run = async () => {
      const token = Cookie.get(ACCESS_TOKEN)

      if (isClientPortal) {
        const sessionUser = getSessionUser()
        if (sessionUser?.clientId) {
          await updateAclNavIds([])
          return
        }

        if (token) {
          const loaded = await loadClientSession()
          if (loaded || isOpenClientBooking) return
        }

        if (isOpenClientBooking) return

        cleanLogin('client')
        return
      }

      if (!token) {
        cleanLogin('staff')
        return
      }

      const sessionUser = getSessionUser()
      if (sessionUser) {
        redirect404IfStaffBlocked(sessionUser, getAclNavIds())
        return
      }

      const loaded = await loadStaffSession()
      if (!loaded) {
        cleanLogin('staff')
        return
      }
      redirect404IfStaffBlocked(loaded.staff, loaded.memberAcls)
    }

    run()
  }, [
    getSessionUser,
    getAclNavIds,
    navigate,
    updateSessionUser,
    navId,
    updateAclNavIds,
    path,
    isClientPortal,
    isOpenClientBooking,
    pathname
  ])

  return <React.Fragment>{component}</React.Fragment>
}

export default AuthGuard
